import { config } from '@/config'
import axios, { AxiosResponse } from 'axios'
import type {
  DashboardSummaryResponse,
  DashboardUserResponse,
  DashboardOrganizationResponse,
  DashboardPaymentResponse,
  DashboardPaymentFailureResponse,
  DashboardSubscriptionResponse,
  DashboardSubscriptionsListResponse,
  DashboardSubscriptionsSyncResponse,
  DashboardCredentialsListResponse,
  DashboardNotificationLogResponse,
  DashboardNotificationLogDetailResponse,
  DashboardMetricsResponse,
  DashboardUsersParams,
  DashboardOrganizationsParams,
  DashboardPaymentsParams,
  DashboardPaymentFailuresParams,
  DashboardSubscriptionsParams,
  DashboardCredentialsParams,
  DashboardMetricsParams,
  DashboardNotificationsParams,
  DashboardExportParams,
  AdminMrrResponse,
  AdminTransactionListResponse,
  SyncTransactionsResponse,
  LinkCustomerResponse,
  GetTransactionsParams
} from '@/types/adminDashboard'

const BASE = `${config.apiUrl}/v1/admin/dashboard`

function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') search.set(k, String(v))
  })
  const q = search.toString()
  return q ? `?${q}` : ''
}

function isForbidden(e: unknown): boolean {
  return axios.isAxiosError(e) && e.response?.status === 403
}

function hasStringMessage(obj: unknown): obj is { message: string } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'message' in obj &&
    typeof (obj as { message: unknown }).message === 'string'
  )
}

function getErrorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e) && hasStringMessage(e.response?.data)) {
    return e.response.data.message
  }
  return fallback
}

export const AdminDashboardAPI = {
  getSummary: async (token: string): Promise<{
    data?: DashboardSummaryResponse
    err?: string
  }> => {
    try {
      const resp: AxiosResponse<DashboardSummaryResponse> = await axios.get(
        `${BASE}/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard summary' }
    }
  },

  getUsers: async (
    token: string,
    params?: DashboardUsersParams
  ): Promise<{ data?: DashboardUserResponse[]; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardUserResponse[]> = await axios.get(
        `${BASE}/users${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard users' }
    }
  },

  getOrganizations: async (
    token: string,
    params?: DashboardOrganizationsParams
  ): Promise<{ data?: DashboardOrganizationResponse[]; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardOrganizationResponse[]> = await axios.get(
        `${BASE}/organizations${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard organizations' }
    }
  },

  getOrganizationById: async (
    token: string,
    id: string
  ): Promise<{ data?: DashboardOrganizationResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<DashboardOrganizationResponse> = await axios.get(
        `${BASE}/organizations/${encodeURIComponent(id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch organization details' }
    }
  },

  getPayments: async (
    token: string,
    params?: DashboardPaymentsParams
  ): Promise<{ data?: DashboardPaymentResponse[]; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardPaymentResponse[]> = await axios.get(
        `${BASE}/payments${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard payments' }
    }
  },

  getPaymentFailures: async (
    token: string,
    params?: DashboardPaymentFailuresParams
  ): Promise<{ data?: DashboardPaymentFailureResponse[]; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardPaymentFailureResponse[]> = await axios.get(
        `${BASE}/payment-failures${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch payment failures' }
    }
  },

  getSubscriptions: async (
    token: string,
    params?: DashboardSubscriptionsParams
  ): Promise<{ data?: DashboardSubscriptionsListResponse; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardSubscriptionsListResponse> = await axios.get(
        `${BASE}/subscriptions${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch subscriptions' }
    }
  },

  getSubscriptionById: async (
    token: string,
    id: string
  ): Promise<{ data?: DashboardSubscriptionResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<DashboardSubscriptionResponse> = await axios.get(
        `${BASE}/subscriptions/${encodeURIComponent(id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch subscription details' }
    }
  },

  syncSubscriptions: async (
    token: string
  ): Promise<{ data?: DashboardSubscriptionsSyncResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<DashboardSubscriptionsSyncResponse> = await axios.post(
        `${BASE}/subscriptions/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to sync subscriptions' }
    }
  },

  deleteSubscription: async (
    token: string,
    id: string
  ): Promise<{ err?: string }> => {
    try {
      await axios.delete(`${BASE}/subscriptions/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` } }
      )
      return {}
    } catch (error) {
      if (isForbidden(error)) return { err: 'Access denied' }
      return { err: 'Unable to remove subscription' }
    }
  },

  linkStripeCustomerToOrganization: async (
    token: string,
    organizationId: string,
    stripeCustomerId: string
  ): Promise<{ err?: string }> => {
    try {
      await axios.post(
        `${BASE}/organizations/${encodeURIComponent(organizationId)}/link-stripe-customer`,
        { stripeCustomerId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return {}
    } catch (error) {
      if (isForbidden(error)) return { err: 'Access denied' }
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return { err: getErrorMessage(error, 'Organization already has a Stripe customer linked') }
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { err: 'Organization not found' }
      }
      return { err: getErrorMessage(error, 'Unable to link organization to Stripe customer') }
    }
  },

  getMetrics: async (
    token: string,
    params?: DashboardMetricsParams
  ): Promise<{ data?: DashboardMetricsResponse; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardMetricsResponse> = await axios.get(
        `${BASE}/metrics${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard metrics' }
    }
  },

  getCredentials: async (
    token: string,
    params?: DashboardCredentialsParams
  ): Promise<{ data?: DashboardCredentialsListResponse; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardCredentialsListResponse> = await axios.get(
        `${BASE}/credentials${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard credentials' }
    }
  },

  getNotifications: async (
    token: string,
    params?: DashboardNotificationsParams
  ): Promise<{ data?: DashboardNotificationLogResponse[]; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<DashboardNotificationLogResponse[]> = await axios.get(
        `${BASE}/notifications${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch dashboard notifications' }
    }
  },

  getNotificationById: async (
    token: string,
    id: string
  ): Promise<{ data?: DashboardNotificationLogDetailResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<DashboardNotificationLogDetailResponse> = await axios.get(
        `${BASE}/notifications/${encodeURIComponent(id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch notification details' }
    }
  },

  /**
   * Export dashboard data as JSON or CSV.
   * For CSV, returns blob and suggested filename (from Content-Disposition or default).
   */
  exportData: async (
    token: string,
    params: DashboardExportParams
  ): Promise<{
    data?: Blob | unknown[]
    err?: string
    filename?: string
  }> => {
    const isCsv = params.format === 'csv'
    const query: Record<string, string | number | undefined> = {
      type: params.type,
      format: params.format ?? 'json',
      startDate: params.startDate,
      endDate: params.endDate,
      organizationId: params.organizationId,
      status: params.status,
      eventTypeCode: params.eventTypeCode,
      limit: params.limit,
      offset: params.offset
    }
    const q = toQuery(query)
    try {
      if (isCsv) {
        const resp = await axios.get<Blob>(`${BASE}/export${q}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        })
        const disposition = (resp as AxiosResponse<Blob>).headers?.['content-disposition']
        let filename = `dashboard-${params.type}.csv`
        if (typeof disposition === 'string' && disposition.includes('filename=')) {
          const match = disposition.match(/filename="?([^";\n]+)"?/)
          if (match?.[1]) filename = match[1].trim()
        }
        return { data: resp.data, filename, err: undefined }
      }
      const resp = await axios.get<unknown[] | Record<string, unknown>>(
        `${BASE}/export${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: Array.isArray(resp.data) ? resp.data : [resp.data], err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to export dashboard data' }
    }
  },

  getMrr: async (token: string): Promise<{ data?: AdminMrrResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<AdminMrrResponse> = await axios.get(`${config.apiUrl}/v1/admin/billing/mrr`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch MRR data' }
    }
  },

  getTransactions: async (
    token: string,
    params?: GetTransactionsParams
  ): Promise<{ data?: AdminTransactionListResponse; err?: string }> => {
    try {
      const q = params ? toQuery(params as Record<string, string | number | undefined>) : ''
      const resp: AxiosResponse<AdminTransactionListResponse> = await axios.get(
        `${config.apiUrl}/v1/admin/billing/transactions${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to fetch transactions' }
    }
  },

  syncTransactions: async (
    token: string,
    params: { startDate: string; endDate: string }
  ): Promise<{ data?: SyncTransactionsResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<SyncTransactionsResponse> = await axios.post(
        `${config.apiUrl}/v1/admin/billing/transactions/sync`,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      return { data: undefined, err: 'Unable to sync transactions' }
    }
  },

  linkStripeCustomer: async (
    token: string,
    organizationId: string,
    payload: { stripeCustomerId: string; setPrimary?: boolean }
  ): Promise<{ data?: LinkCustomerResponse; err?: string }> => {
    try {
      const resp: AxiosResponse<LinkCustomerResponse> = await axios.post(
        `${config.apiUrl}/v1/admin/dashboard/organizations/${encodeURIComponent(organizationId)}/link-stripe-customer`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { data: resp.data, err: undefined }
    } catch (error) {
      if (isForbidden(error)) return { data: undefined, err: 'Access denied' }
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return { data: undefined, err: getErrorMessage(error, 'Invalid request') }
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { data: undefined, err: 'Organization not found' }
      }
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        return { data: undefined, err: getErrorMessage(error, 'Customer already linked to another organization') }
      }
      return { data: undefined, err: 'Unable to link customer' }
    }
  }
}
